import React, { useState, useEffect, useRef, useCallback } from 'react';
import groqService from '../services/groqService';
import voiceService from '../services/voiceService';
import deviceService from '../services/deviceService';
import VoiceCommandHandler from '../services/voiceCommandHandler';
import { toastService } from './Toast';
import './ChatBot.css';

const ChatBot = ({ isVisible, onClose, onLogout, isMinimized: externalMinimized, onToggleMinimize }) => {
  const [isMinimized, setIsMinimized] = useState(externalMinimized || false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true); // Mặc định bật TTS
  const [usedVoiceInput, setUsedVoiceInput] = useState(false); // Theo dõi xem user có dùng voice không
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Vị trí widget
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const widgetRef = useRef(null);
  const ttsEnabledRef = useRef(true); // Ref để luôn có giá trị mới nhất

  // Cập nhật ref khi state thay đổi
  useEffect(() => {
    ttsEnabledRef.current = ttsEnabled;
  }, [ttsEnabled]);

  // Helper function để phát âm nếu TTS được bật
  const speakIfEnabled = useCallback((text) => {
    // Kiểm tra cả ref để đảm bảo có giá trị mới nhất
    if (ttsEnabledRef.current) {
      voiceService.speak(text);
    } else {
      console.log('🔇 TTS is disabled, skipping speech');
    }
  }, []);

  // Hàm điều chỉnh vị trí để widget nằm trong viewport
  const adjustPositionToViewport = useCallback((minimized = false) => {
    const widgetWidth = minimized ? 70 : 380;
    const widgetHeight = minimized ? 70 : 600;
    const maxHeight = Math.min(600, window.innerHeight - 40);
    const actualHeight = minimized ? 70 : maxHeight;
    
    setPosition(prev => {
      let newX = prev.x || 0;
      let newY = prev.y || 0;
      
      // Nếu chưa có position (lần đầu), dùng vị trí mặc định
      if (prev.x === 0 && prev.y === 0) {
        newX = window.innerWidth - widgetWidth - 20;
        newY = window.innerHeight - actualHeight - 20;
      } else {
        // Kiểm tra và điều chỉnh nếu widget tràn ra ngoài viewport
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - actualHeight;
        
        // Điều chỉnh X
        if (newX < 0) {
          newX = 20; // Cách trái 20px
        } else if (newX > maxX) {
          newX = Math.max(20, maxX - 20); // Cách phải 20px
        }
        
        // Điều chỉnh Y
        if (newY < 0) {
          newY = 20; // Cách trên 20px
        } else if (newY > maxY) {
          newY = Math.max(20, maxY - 20); // Cách dưới 20px
        }
      }
      
      return { x: newX, y: newY };
    });
  }, []);

  // Xử lý lệnh điều khiển thiết bị
  const handleDeviceCommand = useCallback(async (command) => {
    // Thêm tin nhắn người dùng vào UI
    const userMessage = {
      id: Date.now(),
      text: command.originalCommand,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Thêm tin nhắn "đang xử lý..."
    const processingMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: processingMessageId,
      text: 'Đang xử lý...',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    }]);

    try {
      const turnOn = command.action === 'turn_on';
      const result = await deviceService.toggleSmartSocket(turnOn);
      
      const response = VoiceCommandHandler.generateResponse(command, result);
      
      // Cập nhật tin nhắn
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: response, isLoading: false }
          : msg
      ));

      // Phát âm phản hồi nếu TTS được bật
      speakIfEnabled(response);
    } catch (error) {
      const errorResponse = `❌ Lỗi: ${error.message}`;
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: errorResponse, isLoading: false, isError: true }
          : msg
      ));
    }
  }, []);

  // Xử lý lệnh hỏi trạng thái thiết bị
  const handleDeviceStatus = useCallback(async (command) => {
    // Thêm tin nhắn người dùng vào UI
    const userMessage = {
      id: Date.now(),
      text: command.originalCommand,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Thêm tin nhắn "đang kiểm tra..."
    const processingMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: processingMessageId,
      text: 'Đang kiểm tra...',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    }]);

    try {
      const status = await deviceService.getSmartSocketStatus();
      const response = VoiceCommandHandler.generateResponse(command, status);
      
      // Cập nhật tin nhắn
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: response, isLoading: false }
          : msg
      ));

      // Phát âm phản hồi nếu TTS được bật
      speakIfEnabled(response);
    } catch (error) {
      const errorResponse = `❌ Lỗi: ${error.message}`;
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: errorResponse, isLoading: false, isError: true }
          : msg
      ));
    }
  }, [speakIfEnabled]);

  // Xử lý lệnh countdown (hẹn giờ nhanh)
  const handleCountdownCommand = useCallback(async (command) => {
    // Thêm tin nhắn người dùng vào UI
    const userMessage = {
      id: Date.now(),
      text: command.originalCommand,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Thêm tin nhắn "đang tạo hẹn giờ nhanh..."
    const processingMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: processingMessageId,
      text: 'Đang tạo hẹn giờ nhanh...',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    }]);

    try {
      const action = command.action === 'countdown_on' ? 'ON' : 'OFF';
      const seconds = command.seconds;
      
      // Trigger countdown event để DeviceDetail nhận được
      const countdownEvent = new CustomEvent('smartSocketCountdown', {
        detail: {
          action: action,
          seconds: seconds
        }
      });
      window.dispatchEvent(countdownEvent);
      
      // Tạo response
      const actionText = action === 'ON' ? 'bật' : 'tắt';
      const timeStr = seconds < 60 ? `${seconds} giây` : seconds < 3600 ? `${Math.floor(seconds / 60)} phút` : `${Math.floor(seconds / 3600)} giờ`;
      const response = `✅ Đã hẹn giờ ${actionText} ổ cắm sau ${timeStr}`;
      
      // Cập nhật tin nhắn
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: response, isLoading: false }
          : msg
      ));

      // Phát âm phản hồi nếu TTS được bật
      speakIfEnabled(response);
    } catch (error) {
      const errorResponse = `❌ Lỗi: ${error.message}`;
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: errorResponse, isLoading: false, isError: true }
          : msg
      ));
    }
  }, [speakIfEnabled]);

  // Xử lý lệnh hẹn giờ
  const handleScheduleCommand = useCallback(async (command) => {
    // Thêm tin nhắn người dùng vào UI
    const userMessage = {
      id: Date.now(),
      text: command.originalCommand,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Thêm tin nhắn "đang tạo lịch..."
    const processingMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: processingMessageId,
      text: 'Đang tạo lịch hẹn giờ...',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    }]);

    try {
      // Tạo scheduled datetime từ hours và minutes
      const { hours, minutes } = command.scheduledTime;
      const now = new Date();
      const scheduled = new Date();
      scheduled.setHours(hours, minutes, 0, 0);
      
      // Nếu thời gian đã qua hôm nay, đặt cho ngày mai
      if (scheduled <= now) {
        scheduled.setDate(scheduled.getDate() + 1);
      }
      
      // Gửi giờ Việt Nam trực tiếp (không convert UTC)
      const year = scheduled.getFullYear();
      const month = String(scheduled.getMonth() + 1).padStart(2, '0');
      const day = String(scheduled.getDate()).padStart(2, '0');
      const hour = String(scheduled.getHours()).padStart(2, '0');
      const minute = String(scheduled.getMinutes()).padStart(2, '0');
      const second = String(scheduled.getSeconds()).padStart(2, '0');
      const scheduledTimeISO = `${year}-${month}-${day}T${hour}:${minute}:${second}`; // "2026-01-03T20:59:00" (VN time)
      const action = command.action === 'schedule_on' ? 'ON' : 'OFF';
      
      const result = await deviceService.createSchedule(action, scheduledTimeISO);
      const response = VoiceCommandHandler.generateResponse(command, result);
      
      // Cập nhật tin nhắn
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: response, isLoading: false }
          : msg
      ));

      // Phát event để DeviceDetail refresh schedules
      if (result && result.success) {
        const scheduleEvent = new CustomEvent('smartSocketScheduleCreated', {
          detail: {
            action: action,
            scheduledTime: scheduledTimeISO
          }
        });
        window.dispatchEvent(scheduleEvent);
      }

      // Phát âm phản hồi nếu TTS được bật
      speakIfEnabled(response);
    } catch (error) {
      const errorResponse = `❌ Lỗi: ${error.message}`;
      setMessages(prev => prev.map(msg => 
        msg.id === processingMessageId 
          ? { ...msg, text: errorResponse, isLoading: false, isError: true }
          : msg
      ));
    }
  }, [speakIfEnabled]);

  // Khởi tạo TTS flag trong voiceService
  useEffect(() => {
    if (voiceService.setShouldSpeak) {
      voiceService.setShouldSpeak(ttsEnabled);
    }
  }, []); // Chỉ chạy một lần khi mount

  // Điều chỉnh vị trí khi resize window
  useEffect(() => {
    const handleResize = () => {
      adjustPositionToViewport(isMinimized);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMinimized, adjustPositionToViewport]);

  // Điều chỉnh vị trí ban đầu khi mount
  useEffect(() => {
    if (isVisible) {
      // Đợi một chút để đảm bảo widget đã render
      setTimeout(() => {
        adjustPositionToViewport(isMinimized);
      }, 100);
    }
  }, [isVisible, adjustPositionToViewport, isMinimized]);

  // Kiểm tra voice support và setup voice service
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const supported = !!SpeechRecognition;
    setVoiceSupported(supported);
    
    if (supported) {
      // Setup voice service callbacks
      voiceService.onResult = async (transcript) => {
        setInputMessage(transcript);
        setIsListening(false);
        setUsedVoiceInput(true); // Đánh dấu user đã dùng voice
        
        // Kiểm tra xem có phải lệnh điều khiển thiết bị không
        const command = VoiceCommandHandler.parseCommand(transcript);
        
        if (command && command.type === 'device_control') {
          // Xử lý lệnh điều khiển thiết bị
          await handleDeviceCommand(command);
        } else         if (command && command.type === 'device_status') {
          // Xử lý lệnh hỏi trạng thái
          await handleDeviceStatus(command);
        } else if (command && command.type === 'countdown') {
          // Xử lý lệnh countdown (hẹn giờ nhanh)
          await handleCountdownCommand(command);
        } else if (command && command.type === 'schedule') {
          // Xử lý lệnh hẹn giờ
          await handleScheduleCommand(command);
        } else {
          // Không phải lệnh điều khiển, gửi cho AI như bình thường
          setTimeout(() => {
            handleSendMessage(transcript);
          }, 300);
        }
      };
      
      voiceService.onStart = () => {
        setIsListening(true);
        setError(null);
      };
      
      voiceService.onEnd = () => {
        setIsListening(false);
      };
      
      voiceService.onError = (errorMsg) => {
        setIsListening(false);
        setError(errorMsg);
      };
    }
    
    return () => {
      voiceService.dispose();
    };
  }, [handleDeviceCommand, handleDeviceStatus, handleCountdownCommand, handleScheduleCommand, speakIfEnabled]);

  // Tin nhắn chào mừng khi mở chatbot
  useEffect(() => {
    if (isVisible && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        text: "Xin chào! Tôi là Bobo, trợ lý AI của Smartify. 🤖\n\n" +
              "Tôi có thể giúp bạn:\n" +
              "• Điều khiển thiết bị nhà thông minh bằng giọng nói\n" +
              "  (Ví dụ: \"Tắt ổ cắm\", \"Bật ổ cắm\")\n" +
              "• Thiết lập tự động hóa\n" +
              "• Quản lý năng lượng\n" +
              "• Trả lời câu hỏi về Smartify\n\n" +
              "Nhấn 🎤 để nói hoặc gõ tin nhắn!",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isVisible]);

  // Auto scroll xuống tin nhắn mới nhất - cải thiện để luôn scroll khi có tin nhắn mới
  const scrollToBottom = useCallback((force = false) => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    
    // Nếu force = true (khi phóng to widget), luôn scroll
    if (force) {
      // Đợi một chút để DOM render xong
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 150);
      return;
    }
    
    // Chỉ scroll nếu user không đang scroll lên trên
    const isNearBottom = container.scrollTop >= maxScroll - 100; // 100px threshold
    
    if (isNearBottom || messages.length <= 1) {
      // Scroll ngay lập tức cho tin nhắn đầu tiên hoặc khi đã ở gần cuối
      container.scrollTop = scrollHeight;
    } else {
      // Scroll mượt mà cho các tin nhắn tiếp theo
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Scroll ngay khi có tin nhắn loading hoặc tin nhắn mới được thêm
  useEffect(() => {
    if (isLoading || messages.length > 0) {
      // Delay nhỏ để đảm bảo DOM đã render
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, messages, scrollToBottom]);

  const handleSendMessage = async (voiceMessage = null) => {
    const message = (voiceMessage || inputMessage).trim();
    if (!message || isLoading) return;

    // Đánh dấu nếu đây là voice message
    const isVoiceMessage = !!voiceMessage;
    if (isVoiceMessage) {
      setUsedVoiceInput(true);
    }

    // Kiểm tra xem có phải lệnh điều khiển thiết bị không
    const command = VoiceCommandHandler.parseCommand(message);
    if (command && command.type === 'device_control') {
      await handleDeviceCommand(command);
      if (!voiceMessage) setInputMessage('');
      return;
    }
    if (command && command.type === 'device_status') {
      await handleDeviceStatus(command);
      if (!voiceMessage) setInputMessage('');
      return;
    }
    if (command && command.type === 'countdown') {
      await handleCountdownCommand(command);
      if (!voiceMessage) setInputMessage('');
      return;
    }
    if (command && command.type === 'schedule') {
      await handleScheduleCommand(command);
      if (!voiceMessage) setInputMessage('');
      return;
    }

    // Thêm tin nhắn người dùng vào UI
    const userMessage = {
      id: Date.now(),
      text: message,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Chỉ xóa input nếu không phải từ voice
    if (!voiceMessage) {
      setInputMessage('');
    } else {
      // Nếu từ voice, xóa sau khi đã thêm vào messages
      setInputMessage('');
    }
    
    setIsLoading(true);
    setError(null);

    // Thêm tin nhắn "đang gõ..." của bot
    const loadingMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: loadingMessageId,
      text: '...',
      isUser: false,
      timestamp: new Date(),
      isLoading: true,
    }]);

    try {
      const response = await groqService.sendMessage(message);
      
      // Cập nhật tin nhắn loading thành phản hồi thật
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessageId 
          ? { ...msg, text: response, isLoading: false }
          : msg
      ));
      
      // Tự động phát âm phản hồi nếu TTS được bật và user đã dùng voice
      if (usedVoiceInput) {
        speakIfEnabled(response);
      }
      
      // Reset flag sau khi phản hồi
      setUsedVoiceInput(false);
    } catch (err) {
      // Xóa tin nhắn loading và thêm tin nhắn lỗi
      setMessages(prev => prev.filter(msg => msg.id !== loadingMessageId));
      
      const errorMessage = {
        id: Date.now() + 2,
        text: err.message.includes('GROQ_API_KEY') 
          ? '⚠️ Vui lòng cấu hình API key trong file .env\n\nThêm dòng sau vào file .env:\nREACT_APP_GROQ_API_KEY=your_api_key_here'
          : `Xin lỗi, đã xảy ra lỗi: ${err.message}\n\nVui lòng thử lại sau.`,
        isUser: false,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    console.log('🗑️ Clear chat button clicked');
    try {
      toastService.confirm(
        'Bạn có chắc muốn xóa toàn bộ lịch sử chat?',
        () => {
          // Xác nhận xóa
          console.log('✅ User confirmed delete');
          groqService.clearHistory();
          setMessages([]);
          setError(null);
          toastService.success('Đã xóa lịch sử chat thành công');
        },
        () => {
          // Hủy bỏ - không làm gì
          console.log('❌ User cancelled delete');
        }
      );
      console.log('✅ Toast confirm called');
    } catch (error) {
      console.error('❌ Error showing toast:', error);
      // Fallback to window.confirm nếu toast lỗi
      if (window.confirm('Bạn có chắc muốn xóa toàn bộ lịch sử chat?')) {
        groqService.clearHistory();
        setMessages([]);
        setError(null);
      }
    }
  };

  const handleToggleVoice = () => {
    if (isListening) {
      voiceService.stopListening();
    } else {
      voiceService.startListening();
    }
  };

  const handleToggleTTS = () => {
    setTtsEnabled(prev => {
      const newValue = !prev;
      // Cập nhật flag trong voiceService
      if (voiceService.setShouldSpeak) {
        voiceService.setShouldSpeak(newValue);
      }
      if (!newValue) {
        // Nếu tắt TTS, dừng phát âm hiện tại
        voiceService.stopSpeaking();
      }
      return newValue;
    });
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    const target = e.target;
    
    // Nếu widget đã minimized, cho phép drag từ bất kỳ đâu trên nút
    if (isMinimized) {
      // Cho phép drag từ nút minimized, nhưng không drag khi double click (để mở)
      if (target.closest('.chatbot-minimize-btn')) {
        e.preventDefault();
        setIsDragging(true);
        
        const widget = widgetRef.current;
        if (widget) {
          const rect = widget.getBoundingClientRect();
          setDragStart({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          });
        }
      }
      return;
    }
    
    // Nếu widget expanded, chỉ cho phép drag từ header
    const isButton = target.closest('button') && !target.closest('.chatbot-header');
    const isInput = target.closest('input') || target.closest('textarea');
    
    if (isButton || isInput) {
      return; // Không drag nếu click vào button hoặc input
    }

    if (target.closest('.chatbot-header')) {
      e.preventDefault();
      setIsDragging(true);
      
      // Lấy vị trí hiện tại của widget
      const widget = widgetRef.current;
      if (widget) {
        const rect = widget.getBoundingClientRect();
        setDragStart({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && dragStart.x !== 0 && dragStart.y !== 0) {
      const widgetWidth = isMinimized ? 70 : 380;
      const widgetHeight = isMinimized ? 70 : 600;
      
      // Tính vị trí mới
      let newX = e.clientX - dragStart.x;
      let newY = e.clientY - dragStart.y;
      
      // Giới hạn trong viewport
      const maxX = window.innerWidth - widgetWidth;
      const maxY = window.innerHeight - widgetHeight;
      
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, dragStart, isMinimized]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
  }, []);

  // Thêm event listeners cho drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; // Ngăn select text khi drag
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Sync với external state nếu có
  useEffect(() => {
    if (externalMinimized !== undefined) {
      setIsMinimized(externalMinimized);
    }
  }, [externalMinimized]);

  const handleToggleMinimize = useCallback(() => {
    setIsMinimized(prev => {
      const newState = !prev;
      
      // Khi mở widget (phóng to), điều chỉnh vị trí để nằm trong viewport
      if (!newState) {
        // Đợi một chút để widget render xong
        setTimeout(() => {
          adjustPositionToViewport(false);
          // Scroll xuống cuối khi phóng to lại (force scroll)
          scrollToBottom(true);
        }, 150);
      } else {
        // Khi thu nhỏ, cũng điều chỉnh để nút tròn nằm trong viewport
        setTimeout(() => {
          adjustPositionToViewport(true);
        }, 50);
      }
      
      if (onToggleMinimize) {
        onToggleMinimize(newState);
      }
      
      return newState;
    });
  }, [adjustPositionToViewport, onToggleMinimize, scrollToBottom]);

  if (!isVisible) return null;

  return (
    <div 
      ref={widgetRef}
      className={`chatbot-widget ${isMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        left: position.x || 'auto',
        top: position.y || 'auto',
        right: position.x ? 'auto' : '20px',
        bottom: position.y ? 'auto' : '20px',
        cursor: isDragging ? 'grabbing' : 'default'
      }}
      onMouseDown={handleMouseDown}
    >
      {isMinimized ? (
        // Minimized state - chỉ hiển thị nút, có thể kéo
        <button 
          className="chatbot-minimize-btn"
          onDoubleClick={handleToggleMinimize} // Double click để mở
          title="Double click để mở, kéo để di chuyển"
        >
          <span className="chatbot-avatar-icon">🤖</span>
          <span className="chatbot-minimize-text">Bobo</span>
        </button>
      ) : (
        // Expanded state - hiển thị full chatbot
        <div className="chatbot-container">
        {/* Header - Có thể kéo từ đây */}
        <div className="chatbot-header" style={{ cursor: 'grab' }}>
          <div className="chatbot-header-left">
            <div className="chatbot-avatar">
              <span className="chatbot-avatar-icon">🤖</span>
            </div>
            <div>
              <h2 className="chatbot-title">Chat with Bobo</h2>
              <p className="chatbot-subtitle">AI Assistant</p>
            </div>
          </div>
          <div className="chatbot-header-actions">
            <button 
              className={`chatbot-btn-icon chatbot-tts-btn ${ttsEnabled ? 'active' : ''}`}
              onClick={handleToggleTTS}
              title={ttsEnabled ? 'Tắt âm thanh' : 'Bật âm thanh'}
            >
              <span style={{ fontSize: '18px' }}>{ttsEnabled ? '🔊' : '🔇'}</span>
            </button>
            <button 
              className="chatbot-btn-icon" 
              onClick={handleClearChat}
              title="Xóa lịch sử"
            >
              🗑️
            </button>
            <button 
              className="chatbot-btn-icon" 
              onClick={handleToggleMinimize}
              title="Thu nhỏ"
            >
              ➖
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages" ref={chatContainerRef}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`chatbot-message ${message.isUser ? 'user' : 'bot'} ${message.isError ? 'error' : ''}`}
            >
              {!message.isUser && (
                <div className="chatbot-message-avatar">
                  <span>🤖</span>
                </div>
              )}
              <div className="chatbot-message-content">
                {message.isLoading ? (
                  <div className="chatbot-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <div className="chatbot-message-text">
                    {message.text.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < message.text.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <div className="chatbot-message-time">
                  {message.timestamp.toLocaleTimeString('vi-VN', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chatbot-input-container">
          {error && (
            <div className="chatbot-error-banner">
              ⚠️ {error}
            </div>
          )}
          <div className="chatbot-input-wrapper">
            {voiceSupported && (
              <button
                className={`chatbot-voice-btn ${isListening ? 'listening' : ''}`}
                onClick={handleToggleVoice}
                disabled={isLoading}
                title={isListening ? 'Đang nghe... (Nhấn để dừng)' : 'Nhấn để nói'}
              >
                {isListening ? (
                  <span className="chatbot-voice-pulse">🎤</span>
                ) : (
                  <span>🎤</span>
                )}
              </button>
            )}
            <textarea
              className="chatbot-input"
              placeholder={isListening ? "Đang nghe..." : "Nhập tin nhắn của bạn..."}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || isListening}
              rows={1}
            />
            <button
              className="chatbot-send-btn"
              onClick={() => handleSendMessage()}
              disabled={!inputMessage.trim() || isLoading || isListening}
            >
              {isLoading ? (
                <span className="chatbot-spinner">⏳</span>
              ) : (
                <span>➤</span>
              )}
            </button>
          </div>
          <p className="chatbot-hint">
            {isListening ? (
              <span style={{ color: '#667eea', fontWeight: 'bold' }}>
                🎤 Đang nghe... Hãy nói vào microphone
              </span>
            ) : voiceSupported ? (
              'Nhấn 🎤 để nói, Enter để gửi, Shift+Enter để xuống dòng'
            ) : (
              'Nhấn Enter để gửi, Shift+Enter để xuống dòng'
            )}
          </p>
        </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;

